import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { LANGUAGES } from '../i18n';

export default function LanguageSwitcher({ variant = 'default' }) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  // Compact variant for navbar
  if (variant === 'compact') {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#F5F3EE] hover:bg-[#EBE8E0] transition-colors text-sm font-medium text-[#1A1817]"
          data-testid="language-switcher-button"
        >
          <span className="text-lg">{currentLang.flag}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setIsOpen(false)}
              />
              
              {/* Dropdown */}
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.15)] border border-[#EBE8E0] overflow-hidden z-50"
              >
                <div className="py-2 max-h-80 overflow-y-auto">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[#F5F3EE] transition-colors ${
                        currentLang.code === lang.code ? 'bg-[#FF7555]/10' : ''
                      }`}
                      data-testid={`lang-option-${lang.code}`}
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <span className="flex-1 text-left text-sm font-medium text-[#1A1817]">
                        {lang.name}
                      </span>
                      {currentLang.code === lang.code && (
                        <Check className="w-4 h-4 text-[#FF7555]" />
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Default full variant
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[#EBE8E0] hover:border-[#FF7555]/30 transition-colors shadow-sm"
        data-testid="language-switcher-button"
      >
        <Globe className="w-4 h-4 text-[#7A7571]" />
        <span className="text-lg">{currentLang.flag}</span>
        <span className="text-sm font-medium text-[#1A1817]">{currentLang.name}</span>
        <ChevronDown className={`w-4 h-4 text-[#7A7571] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute left-0 mt-2 w-56 bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.15)] border border-[#EBE8E0] overflow-hidden z-50"
            >
              <div className="p-2">
                <p className="px-3 py-2 text-xs font-semibold text-[#7A7571] uppercase tracking-wider">
                  Select Language
                </p>
              </div>
              <div className="pb-2 max-h-80 overflow-y-auto">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-[#F5F3EE] transition-colors ${
                      currentLang.code === lang.code ? 'bg-[#FF7555]/10' : ''
                    }`}
                    data-testid={`lang-option-${lang.code}`}
                  >
                    <span className="text-2xl">{lang.flag}</span>
                    <span className="flex-1 text-left font-medium text-[#1A1817]">
                      {lang.name}
                    </span>
                    {currentLang.code === lang.code && (
                      <Check className="w-5 h-5 text-[#FF7555]" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
